import { GetUserType } from '@orbit/api-types'
import { toCents } from '@lib/money'
import type { SplitParticipant, SplitType } from '@orbit/money'

// Money conversion lives in @lib/money and the split types in @orbit/money;
// re-exported so form callers keep one import.
export { toCents, fromCents } from '@lib/money'
export type { SplitParticipant, SplitType } from '@orbit/money'

export interface SplitValidation {
	valid: boolean
	message: string
}

/** Returns members sorted so the current user (if present) is first. */
export const sortMembersWithCurrentFirst = (
	members: GetUserType[],
	currentUserId: number | undefined,
): GetUserType[] =>
	[...members].sort((a, b) => {
		if (a.id === currentUserId) return -1
		if (b.id === currentUserId) return 1
		return 0
	})

// Canonical monogram helper — one home in utils/userNames, re-exported for form callers.
export { getInitials } from '@/utils/userNames'

export const getMemberName = (member: GetUserType, currentUserId: number | undefined): string => {
	const fullName = [member.firstName, member.lastName].filter(Boolean).join(' ')
	return member.id === currentUserId ? `${fullName} (You)` : fullName
}

/**
 * Builds the participants payload from the selected ids and per-user split
 * amounts. Equal splits carry a null amount; Value carries integer cents;
 * Percent carries **whole integer percents** (60 = 60%) — the backend rejects
 * anything that doesn't sum to exactly 100.
 */
export const getParticipantsPayload = (
	selectedParticipants: Set<number>,
	splitType: SplitType,
	splitAmounts: Record<number, string>,
): SplitParticipant[] => {
	const participantIds = Array.from(selectedParticipants)
	if (splitType === 'Equally') {
		return participantIds.map(userId => ({ userId, splitAmount: null }))
	}
	if (splitType === 'Percent') {
		return participantIds.map(userId => ({
			userId,
			splitAmount: Math.round(parseFloat(splitAmounts[userId] || '0')),
		}))
	}
	return participantIds.map(userId => ({
		userId,
		splitAmount: toCents(splitAmounts[userId] || '0'),
	}))
}

/**
 * Single source of truth for split validation across Add/Edit Expense.
 * Messages are identical to the previous inline implementations.
 */
export const getSplitValidation = (
	selectedParticipants: Set<number>,
	splitType: SplitType,
	splitAmounts: Record<number, string>,
	amount: string,
	symbol: string,
): SplitValidation => {
	if (selectedParticipants.size === 0) {
		return { valid: false, message: 'Select at least one participant' }
	}
	if (splitType === 'Equally') {
		return { valid: true, message: `Split evenly between ${selectedParticipants.size} people` }
	}
	if (splitType === 'Percent') {
		const total = Array.from(selectedParticipants).reduce(
			(sum, id) => sum + parseFloat(splitAmounts[id] || '0'),
			0,
		)
		if (Math.abs(total - 100) > 0.01) {
			return { valid: false, message: `Percentages total ${total.toFixed(1)}% (must be 100%)` }
		}
		// The wire carries whole percents (backend requires ints summing to 100),
		// so a fractional percent could never round-trip — block it up front.
		const allWhole = Array.from(selectedParticipants).every(id =>
			Number.isInteger(parseFloat(splitAmounts[id] || '0')),
		)
		if (!allWhole) {
			return { valid: false, message: 'Percentages must be whole numbers' }
		}
		return { valid: true, message: '100% allocated' }
	}
	// Value
	const total = Array.from(selectedParticipants).reduce(
		(sum, id) => sum + parseFloat(splitAmounts[id] || '0'),
		0,
	)
	const expenseAmount = parseFloat(amount || '0')
	if (Math.abs(total - expenseAmount) > 0.01) {
		return {
			valid: false,
			message: `${symbol}${total.toFixed(2)} of ${symbol}${expenseAmount.toFixed(2)} allocated`,
		}
	}
	return { valid: true, message: `${symbol}${total.toFixed(2)} fully allocated` }
}
